import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, Buildings, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_LABEL = {
  office: "Office",
  manufacturing: "Manufacturing",
  warehouse: "Warehouse",
  "data-center": "Data center",
  retail: "Retail",
  fleet: "Fleet",
};

export default function Facilities() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    code: "",
    country: "Global",
    address: "",
    facility_type: "office",
    parent_id: "",
  });

  const load = () => api.get("/facilities").then((r) => {
    setItems(r.data);
    window.dispatchEvent(new CustomEvent("cl:facilities-changed"));
  });

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/facilities", { ...form, parent_id: form.parent_id || null });
      toast.success(t("facilityCreated"));
      setShowForm(false);
      setForm({ name: "", code: "", country: "Global", address: "", facility_type: "office", parent_id: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    await api.delete(`/facilities/${id}`);
    toast.success(t("facilityDeleted"));
    load();
  };

  // Build a simple hierarchy
  const parents = items.filter((f) => !f.parent_id);
  const children = (pid) => items.filter((f) => f.parent_id === pid);

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">Organizational hierarchy</div>
          <h1 className="font-display text-3xl font-semibold mt-1">{t("facilities")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{items.length} {items.length === 1 ? "facility" : "facilities"}</p>
        </div>
        <Button onClick={() => setShowForm((s) => !s)} data-testid="facility-toggle">
          <Plus size={16} className="mr-1" /> {showForm ? t("cancel") : t("newFacility")}
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="facility-form">
              <div className="md:col-span-2 space-y-2">
                <Label>{t("facilityName")}</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="facility-name" />
              </div>
              <div className="space-y-2">
                <Label>{t("facilityCode")}</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. IST-HQ" data-testid="facility-code" />
              </div>
              <div className="space-y-2">
                <Label>{t("facilityType")}</Label>
                <Select value={form.facility_type} onValueChange={(v) => setForm({ ...form, facility_type: v })}>
                  <SelectTrigger data-testid="facility-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABEL).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("country")}</Label>
                <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="facility-country" />
              </div>
              <div className="space-y-2">
                <Label>{t("parentFacility")}</Label>
                <Select value={form.parent_id || "none"} onValueChange={(v) => setForm({ ...form, parent_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="facility-parent"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None (top level)</SelectItem>
                    {items.map((f) => (
                      <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3 space-y-2">
                <Label>{t("address")}</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="facility-address" />
              </div>
              <div className="md:col-span-3">
                <Button type="submit" data-testid="facility-submit">{t("createFacility")}</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {parents.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground text-sm">{t("noFacilities")}</CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {parents.map((f) => (
            <Card key={f.id} data-testid={`facility-card-${f.id}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-2">
                    <Buildings size={20} weight="duotone" className="text-primary mt-0.5" />
                    <div>
                      <div className="font-display font-semibold">{f.name}</div>
                      <div className="text-xs font-mono-data text-muted-foreground uppercase tracking-widest">
                        {TYPE_LABEL[f.facility_type] || f.facility_type} · {f.country}
                        {f.code && ` · ${f.code}`}
                      </div>
                      {f.address && (
                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <MapPin size={12} /> {f.address}
                        </div>
                      )}
                    </div>
                  </div>
                  <button onClick={() => remove(f.id)} className="text-muted-foreground hover:text-destructive" data-testid={`facility-delete-${f.id}`}>
                    <Trash size={16} />
                  </button>
                </div>
                {children(f.id).length > 0 && (
                  <div className="ml-6 pl-3 border-l border-border space-y-2">
                    {children(f.id).map((c) => (
                      <div key={c.id} className="flex items-center justify-between text-sm">
                        <div>
                          <span className="font-medium">{c.name}</span>
                          <span className="text-xs text-muted-foreground font-mono-data ml-2">
                            {TYPE_LABEL[c.facility_type] || c.facility_type}
                          </span>
                        </div>
                        <button onClick={() => remove(c.id)} className="text-muted-foreground hover:text-destructive" data-testid={`facility-delete-${c.id}`}>
                          <Trash size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
